import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { Client } from "../generated/api-client";

// Origine API (host:port o origine pubblica). Viene sempre aggiunto il prefisso /api
// (allineato al mount FastAPI in main.py).
//
// Se REACT_APP_PUBLIC_BASE_URL è "/" (staging same-container) oppure assente,
// usiamo window.location.origin a runtime così l'URL risultante è sempre assoluto.
// Un URL assoluto fa sì che axios non ri-prependa il proprio baseURL, evitando
// il double-prefix /api/api che si verifica con URL relativi come "/api".
const rawBase = (() => {
  const env = process.env.REACT_APP_PUBLIC_BASE_URL || "";
  if (!env || env === "/") {
    return typeof window !== "undefined"
      ? window.location.origin
      : "http://localhost:8000";
  }
  return env.replace(/\/$/, "");
})();
export const API_BASE_URL = rawBase.endsWith("/api") ? rawBase : `${rawBase}/api`;

// Create axios instance with interceptors
export const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
  // Necessario per inviare/ricevere il cookie HttpOnly del refresh token
  // (impostato da /auth/login e /auth/refresh sotto path /api/auth).
  withCredentials: true,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("access_token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with refresh token logic
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };
    const url = originalRequest?.url || "";

    // Evita refresh/redirect per le chiamate di auth (login/register/refresh)
    if (
      url.includes("/auth/login") ||
      url.includes("/auth/register") ||
      url.includes("/auth/refresh")
    ) {
      return Promise.reject(error);
    }

    // Multipart uploads with a File cannot be retried: the FormData body stream
    // is consumed on the first attempt and the retry would send an empty body.
    const isMultipartWithFile =
      typeof FormData !== "undefined" &&
      originalRequest?.data instanceof FormData &&
      Array.from((originalRequest.data as FormData).entries()).some(
        ([, value]) => typeof value !== "string"
      );

    // Se è un errore 401 e non abbiamo già provato a refreshare
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isMultipartWithFile
    ) {
      if (isRefreshing) {
        // Se stiamo già refreshando, metti in coda
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Il refresh token viaggia solo nel cookie HttpOnly: nessun body da
        // inviare, withCredentials si occupa di allegare il cookie.
        const response = await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const { access_token } = response.data;
        localStorage.setItem("access_token", access_token);

        // Aggiorna l'header della richiesta originale
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }

        // Processa la coda
        processQueue(null, access_token);
        isRefreshing = false;

        // Riprova la richiesta originale
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Refresh fallito, logout
        processQueue(refreshError, null);
        isRefreshing = false;
        localStorage.removeItem("access_token");
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Create client with the configured axios instance
export const apiClient = new Client(API_BASE_URL, axiosInstance);
